import Button from '@rio-cloud/rio-uikit/Button';
import useClipboard from '@rio-cloud/rio-uikit/useClipboard';
import classNames from 'classnames';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { closableSuccessNotification } from './ClickableNotifications';
import { type FormInputFieldProperties, FormInputFieldType } from './types';

const copiedFeedbackMessage = (currentValue: string) => {
    closableSuccessNotification(
        <FormattedMessage id={'intl-msg:copy-feedback'}>
            {snippet => {
                return <>{`"${currentValue}" ${snippet}`}</>;
            }}
        </FormattedMessage>
    );
};

const FormInputField = (props: React.PropsWithChildren<FormInputFieldProperties>) => {
    const clipboard = useClipboard();
    const copiedValueRef = useRef(props.currentValue);
    const formGroupClassNames = classNames(
        'form-group margin-bottom-10',
        props.hasError && 'has-error',
        props.hasFeedback && 'has-feedback'
    );
    const lengthValidationClassNames = classNames(
        props.hasError && 'text-color-danger',
        !props.hasError && 'text-color-highlight',
        'input-group-addon'
    );

    useEffect(() => {
        if (clipboard.copied) {
            copiedFeedbackMessage(copiedValueRef.current);
        }
    }, [clipboard.copied]);

    const handleCopyButtonClick = () => {
        copiedValueRef.current = props.currentValue;
        clipboard.copy(props.currentValue);
    };

    return (
        <div className={formGroupClassNames}>
            <label>
                <FormattedMessage id={props.translationId} values={props.messageValues} />
            </label>
            <div className='input-group'>
                {props.icon && <span className='input-group-addon'>{props.icon}</span>}
                {(props.type === undefined || props.type === FormInputFieldType.TEXT) && (
                    <input
                        className='form-control'
                        type='text'
                        readOnly={props.disabled}
                        value={props.currentValue ? props.currentValue : ''}
                        // biome-ignore lint/style/noNonNullAssertion: suppress out of convenience during migration to biome
                        onChange={props.onValueChange ? it => props.onValueChange!(it.currentTarget.value) : () => {}}
                        maxLength={props.maxLength ? props.maxLength : -1}
                        data-cy={props.dataAttribute && props.dataAttribute}
                    />
                )}
                {props.type === FormInputFieldType.TEXTAREA && (
                    <textarea
                        className='form-control'
                        readOnly={props.disabled}
                        value={props.currentValue ? props.currentValue : ''}
                        // biome-ignore lint/style/noNonNullAssertion: suppress out of convenience during migration to biome
                        onChange={props.onValueChange ? it => props.onValueChange!(it.currentTarget.value) : () => {}}
                        maxLength={props.maxLength ? props.maxLength : -1}
                        rows={1}
                        data-cy={props.dataAttribute && props.dataAttribute}
                    />
                )}
                {props.hasLengthValidation && props.maxLength ? (
                    <div className={lengthValidationClassNames}>
                        {props.currentValue === '' ? '' : `${props.currentValue.length}/${props.maxLength}`}
                    </div>
                ) : null}
                {props.withCopyButton ? (
                    <div className='input-group-addon'>
                        <Button
                            bsStyle={Button.MUTED}
                            bsSize={Button.SM}
                            className='position-absolute top-1 right-1'
                            disabled={!props.currentValue}
                            iconOnly
                            iconName='duplicate'
                            onClick={handleCopyButtonClick}
                            data-cy={props.dataAttribute && `${props.dataAttribute}-copy-btn`}
                            autoTrackingKey={props.translationId}
                            aria-label='Copy value'
                        />
                    </div>
                ) : null}
                {props.children}
            </div>
            {props.hasFeedback && props.feedbackMsg && <span className={'help-block'}>{props.feedbackMsg}</span>}
        </div>
    );
};

export default FormInputField;
